import { Collection, MaxString, ModelBase, ObservableCollection, Property, Required, VeryLongString } from '@skylab-shared/core';
import { DdkState } from '../accountancy/ddk-state';
import { JpkVatState } from '../accountancy/jpk-vat-state';
import { CalculationMethod } from '../common/calculation-method';
import { EppState } from '../common/epp-state';
import { DocumentClientModel } from '../document/document-client.model';
import { DocumentPaymentListItemModel } from '../document/document-payment-list-item.model';
import { ExchangeRateModel } from '../exchange-rate/exchange-rate.model';
import { KsefSendSubmissionMode } from '../ksef/ksef-send-submission-mode';
import { SplitPaymentState } from '../sale/split-payment-state';
import { PurchaseBankAccountModel } from './purchase-bank-account.model';
import { PurchaseDocumentKind } from './purchase-document-kind';
import { PurchaseVatTableItemModel } from './purchase-vat-table-item.model';

export class PurchaseExpenseDocumentModel extends ModelBase {
    @Property({ identifiable: true })
    public id: number;

    @Property()
    public calculationMethod: CalculationMethod.Gross | CalculationMethod.Net | CalculationMethod.Manual;

    @Property()
    public cashBasis: boolean;

    @Property(DocumentClientModel, {nullable: true})
    public seller: DocumentClientModel | null;

    @Property(PurchaseBankAccountModel, {nullable: true})
    public bankAccount: PurchaseBankAccountModel | null;

    @Property()
    public communityTransactionId: number;

    @Property()
    public currencyId: number;

    @Property()
    public ddkState: DdkState;

    @Property()
    public deliveryDate: Date;

    @Property()
    public grossValue: number;

    @Property()
    @VeryLongString('data-model.purchase.validation.document-number.length')
    public documentNumber: string;

    @Property(Date, {nullable: true})
    public dueDate: Date | null;

    @Property(Number, {nullable: true})
    public dueDateDays: number | null;

    @Property(ExchangeRateModel, {nullable: true})
    public exchangeRate: ExchangeRateModel | null;

    @Property(String, {nullable: true})
    @VeryLongString('data-model.document.validation.expense-name.length')
    public expenseName: string | null;

    @Property()
    @VeryLongString('data-model.document.validation.external-document-number.length')
    public externalDocumentNumber: string;

    @Property()
    public eppState: EppState;

    @Property()
    public favourite: boolean;

    @Property()
    public fixedAsset: boolean;

    @Property(Date, { nullable: true })
    public issueDate: Date | null;

    @Property()
    public issuedByVatPayer: boolean;

    @Property()
    public jpk_VATState: JpkVatState;

    @Property()
    public jpkVatGroups: string[];

    @Property()
    public readonly kind: PurchaseDocumentKind = PurchaseDocumentKind.Expense;

    @Property()
    @MaxString('data-model.document.validation.note.length')
    public note: string;

    @Property()
    public numberingId: number;

    @Collection({ type: DocumentPaymentListItemModel })
    public payments: ObservableCollection<DocumentPaymentListItemModel>;

    @Property()
    @Required('data-model.purchase.validation.payment-method.required')
    public paymentMethodId: number;

    @Property(Date, { nullable: true })
    public receiptDate: Date | null;

    @Property()
    public splitPayment: SplitPaymentState;

    @Property({ nullable: true })
    public vatDeductionDate: Date;

    @Collection({ type: PurchaseVatTableItemModel })
    public vatTable: ObservableCollection<PurchaseVatTableItemModel>;

    @Property(String, {nullable: true})
    public ksefDocumentId: string | null;

    @Property({ nullable: false })
    public ksefSubmissionMode: KsefSendSubmissionMode;

    @Property({ nullable: true })
    public ksefHeaderId?: number;

    @Property({ nullable: true })
    public dateOfAssigningKsefNumber?: Date;

    @Property({ nullable: true })
    public ksefHash?: string;

    @Property(String, { nullable: true })
    public ksefCollectiveIdentifier?: string | null;

    @Property({nullable: true})
    public originalEInvoiceDocumentId?: number;
}
